import { AxiosError } from 'axios'
import { Alpha3Code } from 'i18n-iso-countries'
import { useCallback } from 'react'

import { useClient } from '../useClient/useClient'
import { useRateLimit } from '../useRateLimit/useRateLimit'

import { getAccountConfig } from '@percent/workplace-giving/api/account/accountConfig/accountConfig'
import { getAccount } from '@percent/workplace-giving/api/auth/getAccount/getAccount'
import { getAccountPartner } from '@percent/workplace-giving/api/auth/getAccountPartner/getAccountPartner'
import { getAccountTerms } from '@percent/workplace-giving/api/auth/getAccountTerms/getAccountTerms'
import { getPartnerConfig } from '@percent/workplace-giving/api/auth/getPartnerConfig/getPartnerConfig'
import { PartnerConfigKeys } from '@percent/workplace-giving/api/auth/getPartnerConfig/getPartnerConfig.types'
import { getPartnerFundraisingConfig } from '@percent/workplace-giving/api/auth/getPartnerFundraisingConfig/getPartnerFundraisingConfig'
import { getPermissions } from '@percent/workplace-giving/api/auth/getPermissions/getPermissions'
import { getGeolocationData } from '@percent/workplace-giving/api/geolocation/getGeolocationData'
import { Account } from '@percent/workplace-giving/context/auth/authContext/AuthContext.types'
import { AuthAction } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import {
  RATE_LIMIT_HIT,
  parseRetryAfter
} from '@percent/workplace-giving/context/client/clientContextController/interceptors/responseInterceptors'
import countries from '@percent/workplace-giving/i18n/countries'
import { maybeUpdatePreferredLanguage } from '@percent/workplace-giving/utils/maybeUpdatePreferredLanguage'
import { getPartnerVolunteeringConfig } from '@percent/workplace-giving/api/auth/getPartnerVolunteeringConfig/getPartnerVolunteeringConfig'
import { PartnerVolunteeringConfigKeys } from '@percent/workplace-giving/api/auth/getPartnerVolunteeringConfig/getPartnerVolunteeringConfig.types'
import { getPartnerDonationMatchingConfig } from '@percent/workplace-giving/api/auth/getPartnerDonationMatchingConfig/getPartnerDonationMatchingConfig'
import { PartnerDonationMatchingConfigKeys } from '@percent/workplace-giving/api/auth/getPartnerDonationMatchingConfig/getPartnerDonationMatchingConfig.types'
import { getGivingProfile } from '@percent/workplace-giving/api/auth/getGivingProfile/getGivingProfile'

export const useLogin = ({ dispatch }: { dispatch: (action: AuthAction) => void }) => {
  const { gsClient, client } = useClient()

  const { setRateLimitHit } = useRateLimit()

  const login = useCallback(async ({ account }: { account?: Account }) => {
    try {
      const [
        acc,
        accConfig,
        partner,
        permissions,
        acceptedTerms,
        geolocationData,
        partnerConfig,
        partnerFundraisingConfig,
        partnerVolunteeringConfig,
        partnerDonationMatchingConfig,
        givingProfile
      ] = await Promise.all([
        account ?? getAccount(gsClient),
        getAccountConfig(gsClient),
        getAccountPartner(gsClient),
        getPermissions(gsClient),
        getAccountTerms(gsClient),
        getGeolocationData(client),
        getPartnerConfig(gsClient),
        getPartnerFundraisingConfig(gsClient),
        getPartnerVolunteeringConfig(gsClient),
        getPartnerDonationMatchingConfig(gsClient),
        getGivingProfile(gsClient)
      ])

      const updatedAccount = await maybeUpdatePreferredLanguage(gsClient, acc)

      dispatch({
        type: 'LOGIN',
        payload: {
          permissions,
          account: updatedAccount,
          partner: {
            ...partner,
            theme: partnerConfig?.data[PartnerConfigKeys.Theme]?.value?.colours,
            bannerImg: partnerConfig?.data[PartnerConfigKeys.HomePageBanner]?.value || null,
            overlayGradientPercentage:
              partnerConfig?.data[PartnerConfigKeys.Theme]?.value?.overlayGradientPercentage ?? null,
            requireOrgFieldsInForms: partnerConfig?.data[PartnerConfigKeys.OrgInTimeLog]?.value || null,
            searchVerifiedOrgs: partnerConfig?.data[PartnerConfigKeys.OrgSearchCheck]?.value || null,
            helpHub: partnerConfig?.data[PartnerConfigKeys.HelpLink]?.value || null,
            homePageBannerContent: partnerConfig?.data[PartnerConfigKeys.HomePageBannerContent]?.value || null,
            calendarIntegration: partnerConfig?.data[PartnerConfigKeys.CalendarIntegration]?.value || null,
            configLogo: partnerConfig?.data[PartnerConfigKeys.Logo]?.value || null,
            publicFundraiser: partnerFundraisingConfig || null,
            volunteeringWorkingHoursCheck:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.VolunteeringActivityWorkingHoursCheck]
                ?.value || null,
            volunteeringPostEventSurveys:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.VolunteeringPostEventSurveys]?.value ||
              null,
            volunteeringWaivers:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.VolunteeringWaivers]?.value || null,
            volunteeringWaitlist:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.VolunteeringWaitlist]?.value || null,
            volunteeringOpportunityDisclosure:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.OpportunityDisclosure]?.value?.text || null,
            volunteeringAllowancePeriodicity:
              partnerVolunteeringConfig?.data[PartnerVolunteeringConfigKeys.VolunteeringAllowance]?.value
                ?.periodicity || null,
            features: partnerConfig?.data[PartnerConfigKeys.Features]?.value || null,
            spotlightedOrgs: partnerConfig?.data[PartnerConfigKeys.SpotlightedOrgs]?.value || null,
            familialDonationMatching:
              partnerDonationMatchingConfig?.data[PartnerDonationMatchingConfigKeys.FamilialDonationMatching]?.value
                ?.enabled ?? null,
            selfServeMatching:
              partnerDonationMatchingConfig?.data[PartnerDonationMatchingConfigKeys.SelfServeMatching]?.value
                ?.enabled ?? null,
            directToCampaignMatchRequests:
              partnerDonationMatchingConfig?.data[PartnerDonationMatchingConfigKeys.DirectToCampaignMatchRequests]
                ?.value?.enabled ?? null,
            donationDateEligibility:
              partnerDonationMatchingConfig?.data[PartnerDonationMatchingConfigKeys.DonationDateEligibility]?.value
                ?.enabled ?? null
          },
          acceptedTerms,
          countryCode: (geolocationData?.countryCode ??
            countries.alpha2ToAlpha3(partner.currencyCode.substring(0, 2))) as Alpha3Code,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          stateCode: geolocationData?.stateCode ?? null,
          accConfig,
          accountCountryCode: accConfig?.country ?? null,
          regions: partnerConfig?.data[PartnerConfigKeys.Regions]?.value
            ? Object.values(partnerConfig.data[PartnerConfigKeys.Regions].value).map(({ id, name, countryCodes }) => ({
                id,
                name,
                countryCodes: Array.from(new Set(countryCodes)) as Alpha3Code[]
              }))
            : null,
          datePeriod: partnerConfig?.data[PartnerConfigKeys.DatePeriodConfig]?.value?.date
            ? {
                currentPeriodStarted: new Date(partnerConfig.data[PartnerConfigKeys.DatePeriodConfig].value.date)
              }
            : undefined,
          givingProfile
        }
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response?.status === 429) {
          setRateLimitHit({
            retryAfter: parseRetryAfter(e.response?.headers?.['Retry-After'])
          })

          return {
            rateLimitHit: true
          }
        }
      }

      if (e.message === RATE_LIMIT_HIT) {
        return {
          rateLimitHit: true
        }
      }
      throw e
    }

    return {}
  }, [])

  return {
    login
  }
}
